import { CheckCircle, ExpandLess, ExpandMore, Lightbulb } from "@mui/icons-material";
import type React from "react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { TOAST_MESSAGES } from "../../../constants/messages";
import { KnowledgeApi } from "../../../functions/api/knowledge";
import type { RootState } from "../../../store";
import { CommonButton } from "../../ui/commonButton";
import styles from "../styles.module.scss";

interface KnowledgeCardProps {
  projectId: string;
  knowledgeId: string;
  title: string;
  metaPrompt: string;
  timestamp?: Date; // オプショナルにして警告を回避
}

const KnowledgeCard: React.FC<KnowledgeCardProps> = ({
  projectId,
  knowledgeId,
  title,
  metaPrompt,
}) => {
  const { currentUser } = useSelector((state: RootState) => state.auth);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isActivated, setIsActivated] = useState(false);
  const knowledgeApi = new KnowledgeApi();

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleActivateKnowledge = async () => {
    setIsLoading(true);

    try {
      const result = await knowledgeApi.updateIsActive(
        currentUser.organization_id,
        projectId,
        knowledgeId,
        true, // 有効化のみ
      );

      if (result === true) {
        toast.success(TOAST_MESSAGES.KNOWLEDGE.ACTIVATED);
        // ボタンを無効化または非表示にする
        setIsActivated(true);
      }
    } catch (_error: any) {
      toast.error(TOAST_MESSAGES.KNOWLEDGE.ACTIVATE_FAILED);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles["knowledgeCard"]}>
      {/* ヘッダー */}
      <div className={styles["knowledgeCardHeader"]}>
        <div className={styles["knowledgeCardIcon"]}>
          <Lightbulb />
        </div>
        <span className={styles["knowledgeCardStatus"]}>ナレッジが生成されました</span>
      </div>

      {/* タイトル */}
      <div className={styles["knowledgeCardTitle"]}>{title}</div>

      {/* アクションボタン（詳細ボタンと有効化ボタンを横並び） */}
      <div className={styles["knowledgeCardActions"]}>
        <button className={styles["knowledgeCardDetailButton"]} onClick={handleToggleExpanded}>
          {isExpanded ? (
            <>
              <ExpandLess />
              詳細を閉じる
            </>
          ) : (
            <>
              <ExpandMore />
              詳細を見る
            </>
          )}
        </button>

        {/* 有効化状態 */}
        {isActivated ? (
          <div className={styles["knowledgeActivatedStatus"]}>
            <CheckCircle />
            有効済み
          </div>
        ) : (
          <CommonButton
            onClick={handleActivateKnowledge}
            disabled={isLoading}
            icon={<CheckCircle />}
          >
            {isLoading ? "有効化中..." : "有効化する"}
          </CommonButton>
        )}
      </div>

      {/* 詳細内容（展開時） */}
      {isExpanded && (
        <div className={styles["knowledgeCardDetails"]}>
          <div className={styles["knowledgeCardMetaPrompt"]}>
            <h4 className={styles["knowledgeCardMetaPromptTitle"]}>メタプロンプト:</h4>
            <div className={styles["knowledgeCardMetaPromptContent"]}>{metaPrompt}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeCard;
